import { FileDown, RefreshCw, Save, Settings } from "lucide-react";
import { DiffResult } from "../lib/diff";
import { AppState, keyOf } from "../types/state";
import { AddRemoveCard, ModifiedCard } from "./ChangeCard";

interface Props {
  state: AppState;
  diff: DiffResult;
  refreshing: boolean;
  onRefresh: () => void;
  onSave: () => void;
  onExport: () => void;
  onOpenSettings: () => void;
  onGoToRow: (key: string) => void;
}

export function DiffViewer({
  state,
  diff,
  refreshing,
  onRefresh,
  onSave,
  onExport,
  onOpenSettings,
  onGoToRow,
}: Props) {
  const total = diff.added.length + diff.modified.length + diff.removed.length;
  const refVersion = state.versions[state.versions.length - 1];

  return (
    <div className="h-full flex flex-col">
      <div className="flex items-center justify-between px-3 py-2 border-b border-stone-200 bg-white">
        <div className="min-w-0">
          <h1 className="font-serif-title text-base text-stone-800 truncate">
            {state.config.dataTableName || state.config.dataSheetName}
          </h1>
          <div className="text-xs text-stone-500 truncate">
            Référence :{" "}
            {refVersion
              ? refVersion.label
              : new Date(state.config.lastOpenedAt).toLocaleDateString("fr-FR")}
          </div>
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={onRefresh}
            className="p-1.5 hover:bg-stone-100 rounded"
            title="Rafraîchir"
            disabled={refreshing}
            aria-label="Rafraîchir"
          >
            <RefreshCw className={`w-4 h-4 text-stone-700 ${refreshing ? "animate-spin" : ""}`} />
          </button>
          <button
            onClick={onOpenSettings}
            className="p-1.5 hover:bg-stone-100 rounded"
            title="Paramètres"
            aria-label="Paramètres"
          >
            <Settings className="w-4 h-4 text-stone-700" />
          </button>
        </div>
      </div>

      <div className="px-3 py-3 border-b border-stone-200 space-y-3 bg-stone-50">
        <div className="border border-stone-200 bg-white rounded-md p-3">
          <div className="text-sm text-stone-800 font-medium">
            {total} changement{total !== 1 ? "s" : ""}
          </div>
          <div className="text-xs text-stone-600 mt-1 space-y-0.5">
            <div>
              <span className="inline-block w-2 h-2 rounded-full bg-emerald-500 mr-2" />
              {diff.added.length} ajout{diff.added.length !== 1 ? "s" : ""}
            </div>
            <div>
              <span className="inline-block w-2 h-2 rounded-full bg-amber-500 mr-2" />
              {diff.modified.length} modif{diff.modified.length !== 1 ? "s" : ""}
            </div>
            <div>
              <span className="inline-block w-2 h-2 rounded-full bg-rose-500 mr-2" />
              {diff.removed.length} retrait{diff.removed.length !== 1 ? "s" : ""}
            </div>
          </div>
        </div>

        <div className="flex gap-2">
          <button
            onClick={onSave}
            disabled={total === 0}
            className="flex-1 bg-stone-800 text-white rounded px-2 py-1.5 text-xs hover:bg-stone-700 disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-1.5"
          >
            <Save className="w-3.5 h-3.5" /> Sauvegarder
          </button>
          <button
            onClick={onExport}
            className="flex-1 border border-stone-300 bg-white rounded px-2 py-1.5 text-xs hover:bg-stone-50 flex items-center justify-center gap-1.5"
          >
            <FileDown className="w-3.5 h-3.5" /> Exporter
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-3 space-y-4">
        {total === 0 && (
          <div className="text-center text-sm text-stone-500 mt-8">
            Aucun changement depuis la dernière version.
          </div>
        )}

        {diff.added.length > 0 && (
          <section className="space-y-2">
            <h2 className="text-xs uppercase tracking-wide text-emerald-800">
              Ajouts ({diff.added.length})
            </h2>
            {diff.added.map((r, i) => (
              <AddRemoveCard
                key={i}
                kind="added"
                row={r}
                keyColumns={state.config.keyColumns}
                onGoToRow={() => onGoToRow(keyOf(r, state.config.keyColumns))}
              />
            ))}
          </section>
        )}

        {diff.modified.length > 0 && (
          <section className="space-y-2">
            <h2 className="text-xs uppercase tracking-wide text-amber-800">
              Modifs ({diff.modified.length})
            </h2>
            {diff.modified.map((m) => (
              <ModifiedCard
                key={m.key}
                modified={m}
                keyColumns={state.config.keyColumns}
                onGoToRow={() => onGoToRow(m.key)}
              />
            ))}
          </section>
        )}

        {diff.removed.length > 0 && (
          <section className="space-y-2">
            <h2 className="text-xs uppercase tracking-wide text-rose-800">
              Retirés ({diff.removed.length})
            </h2>
            {diff.removed.map((r, i) => (
              <AddRemoveCard key={i} kind="removed" row={r} keyColumns={state.config.keyColumns} />
            ))}
          </section>
        )}
      </div>
    </div>
  );
}
